import { type PlanType } from '@/types';

import { type PlanFeatureType } from './consts';
import { usePremiumAccess } from './usePremiumAccess';
import { useUserPlanManager } from './UserPlanManagerProvider';

export const useUpsellingGuard = () => {
  const { userPlanManager } = useUserPlanManager();
  const { openFeaturePaywall } = usePremiumAccess();

  return <T>(
    action: () => T,
    options: {
      feature: PlanFeatureType;
      from: string;
      /**
       * 요구 등급을 직접 지정한다. 생략하면 `feature` 에서 유도한다.
       *
       * ⚠️ 이름은 "가격 카드 기본 선택"에서 왔지만, 이 경로에서는 **요구 등급 자체**가 된다.
       * 이용권 매칭(`hasEnoughPlan`)도 이 값을 쓰므로, BASIC 기능에 'PRO' 를 넘기면
       * BASIC 이용권 보유자가 이용권 안내를 못 받는다. 카드만 바꾸고 싶은 게 아니라면 넘기지 말 것.
       */
      initialPlanType?: Exclude<PlanType, 'FREE'>;
      moreConditionForGuard?: boolean;
      onGuard?: () => void;
    },
  ): T | void => {
    const canAccessFeature = userPlanManager.canAccessFeature(options.feature);
    const shouldGuard =
      !canAccessFeature && (options.moreConditionForGuard ?? true);
    if (shouldGuard) {
      if (options.onGuard) {
        options.onGuard();
        return;
      }
      // 판정·조회는 비동기지만 가드 자체는 동기 계약을 유지한다.
      // 제네릭 T 는 통과 분기로만 흐르므로, 여기서 await 해도 얻을 게 없고
      // 소비처 12곳의 시그니처만 오염된다.
      void openFeaturePaywall({
        requiredPlan:
          options.initialPlanType ??
          userPlanManager.getFeaturePlanType(options.feature),
        from: options.from,
      });
      return;
    }
    return action();
  };
};
